import PropertyIsNotRegistered from '../PropertyIsNotRegistered';
import HundredBricksAPI from '../../HundredBricksAPI';
import { APIError } from '../../../api';
import { LeaseholdersError } from '../leaseholders';
import { AmenitiesError, Amenity } from '../amenities';
import ManagementLogs, { ManagementLog, ManagementLogsError } from '../managementLogs';
import PropertyDeliveries, { PropertyDeliveriesError } from './propertyDeliveries';
import { Quotation, QuotationError } from './quotation';
import CrowdfundingReportStatus, { CrowdfundingReportStatusError } from './latestCrowdfundingPropertyStatusReport';
import { InvalidCouponId, InvestorWithInsufficientFunds, InvestorCoupon, InvestorCouponIsNotRegistered, InvestorCouponsError, PropertyWithInsufficientBricks } from './investorCoupons';
import { PresaleLeaseholder } from './presalelLeaseholders';
import { CrowdfundingProperties, CrowdfundingPropertiesError, CrowdfundingProperty } from './crowdfundingProperty';
import { Progress, ProgressError, PropertyProgressIsNotRegistered } from './progress';
import CreateInvestment, { CreateInvestmentError, InvalidTokenCode, CodeValidationLimitReached, BrickNumberIsUnderMinimumBricks, BrickNumberExceedsMaximumBricks, InvalidPaymentPlanId, PropertyIsNotActive, InvalidPaymentPlanStatus, InvestorDoesNotOwnCoupon, InvestmentLimitAmountExceeded, InvestorCannotOperate, InvestorExceededLimits, PromotionalCouponDoesNotExists, PaymentPlanNotFound, CouponAlreadyExpired, CouponAlreadyUsed, CouponIsDisabled, CouponIsNotActive, BrickNumberExceedsAvailableBricks, PropertyHasNoAvailableBricks, NotEnoughFunds, InvalidBricksQuantity, CheckoutError } from './createInvestment';
class CrowdfundingPropertiesAPI extends HundredBricksAPI {
    async getCrowdfundingProperties() {
        try {
            const { data } = await this.client.get('v2/crowdfundingProperties');
            const crowdfundingProperties = data.items.map(property => (new CrowdfundingProperty(property)));
            return new CrowdfundingProperties(crowdfundingProperties);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new CrowdfundingPropertiesError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new CrowdfundingPropertiesError('Unexpected error when obtaining crowdfunding properties', { cause: error });
            }
            else {
                throw new CrowdfundingPropertiesError(`Unexpected error when obtaining crowdfunding properties: '${error}'`);
            }
        }
    }
    async getProgress(propertyId) {
        try {
            const { data } = await this.client.get(`v2/crowdfundingProperties/${propertyId}/progress`);
            return new Progress(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'PROGRESS_NOT_FOUND':
                        throw new PropertyProgressIsNotRegistered(propertyId, { cause: error });
                    default:
                        throw new ProgressError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new ProgressError('Unexpected property progress error', { cause: error });
            }
            else {
                throw new ProgressError(`Unexpected property progress error: '${error}'`);
            }
        }
    }
    async getLeaseholders(propertyId) {
        try {
            const { data } = await this.client.get(`v2/crowdfundingProperties/${propertyId}/leaseholders`);
            return data.items.map(leaseholder => new PresaleLeaseholder(leaseholder));
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'CROWDFUNDING_PROPERTY_NOT_FOUND':
                        throw new PropertyIsNotRegistered(propertyId, 'crowdfunding', { cause: error });
                    default:
                        throw new LeaseholdersError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new LeaseholdersError('Unexpected presale property leaseholders error', { cause: error });
            }
            else {
                throw new LeaseholdersError(`Unexpected presale property leaseholders error: '${error}'`);
            }
        }
    }
    async getAmenities(propertyId) {
        try {
            const { data } = await this.client.get(`v2/crowdfundingProperties/${propertyId}/amenities`);
            return data.items.map(amenity => this.buildPropertyAmenity(amenity));
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new AmenitiesError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new AmenitiesError('Unexpected presale property amenities error', { cause: error });
            }
            else {
                throw new AmenitiesError(`Unexpected presale property amenities error: '${error}'`);
            }
        }
    }
    async getInvestorCoupons(propertyId) {
        try {
            const { data } = await this.client.get(`v1/crowdfundingProperties/${propertyId}/promotionalCoupons`);
            return data.map(coupon => new InvestorCoupon(coupon));
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new InvestorCouponsError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new InvestorCouponsError('Unexpected error when obtaining crowdfunding investor coupons', { cause: error });
            }
            else {
                throw new InvestorCouponsError(`Unexpected error when obtaining crowdfunding investor coupons: '${error}'`);
            }
        }
    }
    async validateInvestorCoupon(couponId) {
        try {
            const { data } = await this.client.get(`v1/promotionalCoupons/${couponId}/validate`);
            return new InvestorCoupon(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INVALID_ID':
                        throw new InvalidCouponId(couponId, { cause: error });
                    case 'PROMOTIONAL_COUPON_NOT_FOUND':
                        throw new InvestorCouponIsNotRegistered({ cause: error });
                    case 'INSUFFICIENT_FUNDS':
                        throw new InvestorWithInsufficientFunds({ cause: error });
                    case 'INSUFFICIENT_BRICKS':
                        throw new PropertyWithInsufficientBricks({ cause: error });
                    default:
                        throw new InvestorCouponsError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new InvestorCouponsError('Unexpected error when validating crowdfunding investor coupon', { cause: error });
            }
            else {
                throw new InvestorCouponsError(`Unexpected error when validating crowdfunding investor coupon: '${error}'`);
            }
        }
    }
    async calculateQuotation(propertyId, bricksQuantity) {
        try {
            const { data } = await this.client.get(`v1/crowdfundingProperties/${propertyId}/quoting?bricks=${bricksQuantity}`);
            return new Quotation(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new QuotationError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new QuotationError('Unexpected error when calculating crowdfunding quotation', { cause: error });
            }
            else {
                throw new QuotationError(`Unexpected error when calculating crowdfunding quotation: '${error}'`);
            }
        }
    }
    async createInvestment(payload) {
        try {
            const { data } = await this.client.post('v1/crowdfundingInvestments', payload);
            return new CreateInvestment(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INVALID_CODE':
                        throw new InvalidTokenCode({ cause: error });
                    case 'CODE_VALIDATION_LIMIT_REACHED':
                        throw new CodeValidationLimitReached({ cause: error });
                    case 'BRICK_NUMBER_UNDER_MINIMUM':
                        throw new BrickNumberIsUnderMinimumBricks({ cause: error });
                    case 'BRICK_NUMBER_EXCEEDS_MAXIMUM':
                        throw new BrickNumberExceedsMaximumBricks({ cause: error });
                    case 'INVALID_PAYMENT_PLAN_ID':
                        throw new InvalidPaymentPlanId({ cause: error });
                    case 'PROPERTY_NOT_ACTIVE':
                        throw new PropertyIsNotActive({ cause: error });
                    case 'INVALID_PAYMENT_PLAN_STATUS':
                        throw new InvalidPaymentPlanStatus({ cause: error });
                    case 'TRADER_DOES_NOT_OWN_COUPON':
                        throw new InvestorDoesNotOwnCoupon({ cause: error });
                    case 'INVESTMENT_LIMIT_AMOUNT_EXCEEDED':
                        throw new InvestmentLimitAmountExceeded({ cause: error });
                    case 'INVESTOR_CANNOT_OPERATE':
                        throw new InvestorCannotOperate({ cause: error });
                    case 'INVESTOR_EXCEEDS_OPERATIONS_AMOUNT_ALLOWED':
                        throw new InvestorExceededLimits({ cause: error });
                    case 'PROMOTIONAL_COUPON_NOT_FOUND':
                        throw new PromotionalCouponDoesNotExists({ cause: error });
                    case 'PAYMENT_PLAN_NOT_FOUND':
                        throw new PaymentPlanNotFound({ cause: error });
                    case 'COUPON_IS_EXPIRED':
                        throw new CouponAlreadyExpired({ cause: error });
                    case 'COUPON_ALREADY_USED':
                        throw new CouponAlreadyUsed({ cause: error });
                    case 'COUPON_IS_DISABLED':
                        throw new CouponIsDisabled({ cause: error });
                    case 'COUPON_IS_NOT_ACTIVE':
                        throw new CouponIsNotActive({ cause: error });
                    case 'BRICK_NUMBER_EXCEEDS_AVAILABLE':
                        throw new BrickNumberExceedsAvailableBricks({ cause: error });
                    case 'PROPERTY_HAS_NO_AVAILABLE_BRICKS':
                        throw new PropertyHasNoAvailableBricks({ cause: error });
                    case 'NOT_ENOUGH_FUNDS':
                        throw new NotEnoughFunds({ cause: error });
                    case 'INVALID_BRICKS_QUANTITY':
                        throw new InvalidBricksQuantity({ cause: error });
                    case 'CANT_PROCESS_TRADING':
                        throw new CheckoutError({ cause: error });
                    default: throw new CreateInvestmentError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new CreateInvestmentError('Unexpected error when creating crowdfunding investment', { cause: error });
            }
            else {
                throw new CreateInvestmentError(`Unexpected error when creating crowdfunding investment: '${error}'`);
            }
        }
    }
    async getPropertyManagementLogs(propertyId) {
        try {
            const { data } = await this.client.get(`v2/crowdfundingProperties/${propertyId}/managementLogs`);
            const managementLogs = data.items.map(log => new ManagementLog(log));
            return new ManagementLogs(managementLogs);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new ManagementLogsError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new ManagementLogsError('Unexpected error when obtaining crowdfunding property management logs', { cause: error });
            }
            else {
                throw new ManagementLogsError(`Unexpected error when obtaining crowdfunding property management logs: '${error}'`);
            }
        }
    }
    async getPropertyDeliveries(propertyId) {
        try {
            const { data } = await this.client.get(`v1/crowdfundingProperties/${propertyId}/crowdfundingDeliveries`);
            return new PropertyDeliveries(data.crowdfundingDeliveries);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new PropertyDeliveriesError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new PropertyDeliveriesError('Unexpected error when obtaining crowdfunding property deliveries', { cause: error });
            }
            else {
                throw new PropertyDeliveriesError(`Unexpected error when obtaining crowdfunding property deliveries: '${error}'`);
            }
        }
    }
    async getLatestCrowdfundingPropertyStatusReport(propertyId) {
        try {
            const { data } = await this.client.get(`v2/crowdfundingProperties/${propertyId}/statusReports/latest`);
            return new CrowdfundingReportStatus(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new CrowdfundingReportStatusError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new CrowdfundingReportStatusError('Unexpected error when obtaining crowdfunding property status report', { cause: error });
            }
            else {
                throw new CrowdfundingReportStatusError(`Unexpected error when obtaining crowdfunding property status report: '${error}'`);
            }
        }
    }
    buildPropertyAmenity(amenity) {
        return new Amenity({ ...amenity, property: amenity.crowdfundingProperty });
    }
}
export default CrowdfundingPropertiesAPI;
